import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/hooks/useTheme';
import { useLanguage } from '@/hooks/useLanguage';
import { useSearchPlaceholder } from '@/hooks/useSearchPlaceholder';
import { getNotifications, getAuthMe, resolveImageUrl } from '@/api';
import SearchModal from './SearchModal';
import DateTimeModal from './DateTimeModal';
import AiTypingSubtitle from './AiTypingSubtitle';

interface HeaderProps {
  isDarkMode?: boolean;
  setIsDarkMode?: (value: boolean) => void;
}

interface UserInfo {
  name: string;
  avatar?: string;
  userType: string;
  userId: string;
  email?: string;
}

const langs = [
  { code: 'fa', name: 'Persian', flag: '🇮🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
];

export default function Header({ isDarkMode: propIsDarkMode, setIsDarkMode: propSetIsDarkMode }: HeaderProps) {
  const navigate = useNavigate();
  const { isDark: themeDark, toggleTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const { currentItem: searchItem, isReady: searchDataReady } = useSearchPlaceholder();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isDarkMode = propIsDarkMode ?? themeDark;
  const isRTL = i18n.language === 'fa' || i18n.language === 'ar';

  // Check if we're on the search results page and get the current query
  const isSearchPage = location.pathname === '/search';
  const isStoresPage = location.pathname === '/stores';
  const searchPageQuery = isSearchPage ? (searchParams.get('q') || '') : '';

  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [inlineFilterTrigger, setInlineFilterTrigger] = useState(0);
  const [searchCategory, setSearchCategory] = useState<number | null>(null);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showDateTimeModal, setShowDateTimeModal] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showSearchWithFilters, setShowSearchWithFilters] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiMessages, setAiMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const aiModalRef = useRef<HTMLDivElement>(null);
  const aiMessagesEndRef = useRef<HTMLDivElement>(null);
  const [showSearchRow, setShowSearchRow] = useState(true);
  const lastScrollY = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const scrollIgnoreUntil = useRef(0);
  const showSearchRowRef = useRef(true);
  /** Document offset for fixed header — a bit above expanded height so top content isn't clipped. */
  const HEADER_H_EXPANDED = 108;
  const HEADER_H_COLLAPSED = 64;
  const HEADER_ANIM_MS = 300;
  const HEADER_SPACER_H = 124;

  // AI star icon — periodic 90° rotation at random intervals
  const [aiStarRotation, setAiStarRotation] = useState(0);
  const [rippleKey, setRippleKey] = useState(0);
  const aiStarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleNextAiStarSpin = useCallback(() => {
    const minDelay = 2000;
    const maxDelay = 5000;
    const randomDelay = Math.floor(Math.random() * (maxDelay - minDelay + 1)) + minDelay;
    aiStarTimerRef.current = setTimeout(() => {
      setAiStarRotation(prev => prev + 90);
      scheduleNextAiStarSpin();
    }, randomDelay);
  }, []);

  useEffect(() => {
    scheduleNextAiStarSpin();
    return () => {
      if (aiStarTimerRef.current) clearTimeout(aiStarTimerRef.current);
    };
  }, [scheduleNextAiStarSpin]);

  // Trigger ripple burst AFTER the spin finishes (0.7s spin duration)
  useEffect(() => {
    const t = setTimeout(() => {
      setRippleKey(prev => prev + 1);
    }, 700);
    return () => clearTimeout(t);
  }, [aiStarRotation]);

  const [user, setUser] = useState<UserInfo | null>(() => {
    const token = localStorage.getItem('token');
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    if (!token || !isLoggedIn) return null;
    return {
      name: localStorage.getItem('userName') || '',
      avatar: localStorage.getItem('userAvatar') || undefined,
      userType: (localStorage.getItem('userType') || 'customer').toLowerCase(),
      userId: localStorage.getItem('userId') || '',
    };
  });

  const [savedAddress, setSavedAddress] = useState('');
  const [savedDateTime, setSavedDateTime] = useState('');
  const [formattedScheduleDate, setFormattedScheduleDate] = useState('');
  const [pickupMode, setPickupMode] = useState<'courier' | 'scheduled' | 'self'>(() => {
    const pm = localStorage.getItem('pickupMode');
    if (pm === 'self') return 'self';
    if (pm === 'scheduled') return 'scheduled';
    return 'courier';
  });

  const [scheduledSubMode, setScheduledSubMode] = useState<'courier' | 'self'>(() => {
    const ssm = localStorage.getItem('scheduledSubMode');
    return ssm === 'self' ? 'self' : 'courier';
  });

  // Mode toggle - using framer-motion layoutId for native smooth animation
  const modeContainerRef = useRef<HTMLDivElement>(null);

  const menuRef = useRef<HTMLDivElement>(null);

  // Load saved data
  useEffect(() => {
    const update = () => {
      setSavedAddress(localStorage.getItem('selectedDeliveryAddress') || '');
      const date = localStorage.getItem('selectedDeliveryDate') || '';
      const time = localStorage.getItem('selectedDeliveryTime') || '';
      if (date && time) setSavedDateTime(`${date} ${time}`);
      else if (time) setSavedDateTime(time);
      else setSavedDateTime('');

      // Format date for display in scheduled toggle
      if (date) {
        const d = new Date(date + 'T00:00:00');
        const isToday = d.toDateString() === new Date().toDateString();
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const isTomorrow = d.toDateString() === tomorrow.toDateString();
        let dayLabel = '';
        if (isToday) dayLabel = t('datetime.today') || 'Today';
        else if (isTomorrow) dayLabel = t('datetime.tomorrow') || 'Tomorrow';
        else dayLabel = d.toLocaleDateString(i18n.language === 'fa' ? 'fa-IR' : 'en-US', { weekday: 'short' });
        const monthLabel = d.toLocaleDateString(i18n.language === 'fa' ? 'fa-IR' : 'en-US', { month: 'short' });
        const dayNum = String(d.getDate());
        setFormattedScheduleDate(`${dayLabel}, ${monthLabel} ${dayNum}`);
      } else {
        setFormattedScheduleDate('');
      }

      const pm = localStorage.getItem('pickupMode');
      if (pm === 'courier' || pm === 'self' || pm === 'scheduled') setPickupMode(pm);
      const ssm = localStorage.getItem('scheduledSubMode');
      if (ssm === 'courier' || ssm === 'self') setScheduledSubMode(ssm);
    };
    update();
    window.addEventListener('storage', update);
    window.addEventListener('deliveryAddressChanged', update);
    window.addEventListener('addressCoordsChanged', update);
    return () => {
      window.removeEventListener('storage', update);
      window.removeEventListener('deliveryAddressChanged', update);
      window.removeEventListener('addressCoordsChanged', update);
    };
  }, [t, i18n.language]);

  // Load user
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('token');
      const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
      if (!token || !isLoggedIn) { setUser(null); return; }
      const storedName = localStorage.getItem('userName') || '';
      const storedType = localStorage.getItem('userType') || 'customer';
      const storedId = localStorage.getItem('userId') || '';
      const storedAvatar = localStorage.getItem('userAvatar') || '';
      setUser({ name: storedName, avatar: storedAvatar || undefined, userType: storedType.toLowerCase(), userId: storedId });
      try {
        const me = await getAuthMe();
        if (me && typeof me === 'object') {
          const freshName = me.name || me.username || me.displayName || storedName;
          const freshAvatar = me.avatar || me.avatarUrl || storedAvatar;
          const freshType = (me.role || me.userType || storedType || 'customer').toString().toLowerCase();
          if (freshName || freshAvatar) {
            setUser({ name: freshName || storedName, avatar: freshAvatar || undefined, userType: freshType, userId: me.id?.toString() || me.userId?.toString() || storedId });
            if (freshAvatar) localStorage.setItem('userAvatar', freshAvatar);
            if (freshName) localStorage.setItem('userName', freshName);
          }
        } else {
          // getAuthMe returned null — session is dead, clear user state
          setUser(null);
        }
      } catch {
        // getAuthMe threw — session is dead, clear user state
        setUser(null);
      }
    };
    loadUser();
    const onAuth = () => loadUser();
    window.addEventListener('storage', loadUser);
    window.addEventListener('authChanged', onAuth);
    return () => {
      window.removeEventListener('storage', loadUser);
      window.removeEventListener('authChanged', onAuth);
    };
  }, []);

  // Notifications
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const raw = await getNotifications(1, 1);
        let count = 0;
        if (raw && typeof raw === 'object') {
          if (typeof raw.unreadCount === 'number') count = raw.unreadCount;
          else if (Array.isArray(raw.notifications)) count = raw.notifications.filter((n: any) => !n.isRead).length;
          else if (Array.isArray(raw)) count = raw.filter((n: any) => !n.isRead).length;
        }
        setUnreadCount(count);
      } catch { setUnreadCount(0); }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, []);

  // Close settings on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowSettingsMenu(false);
        setShowLangMenu(false);
      }
    };
    if (showSettingsMenu) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showSettingsMenu]);

  // Auto-scroll chat
  useEffect(() => {
    if (aiMessagesEndRef.current) {
      aiMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [aiMessages]);

  // Auto-focus search input when expanded
  useEffect(() => {
    if (isSearchExpanded) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 250);
      return () => clearTimeout(t);
    }
  }, [isSearchExpanded]);

  // Listen for custom event to open search with category
  useEffect(() => {
    const handleOpenSearch = (e: CustomEvent) => {
      const catId = e.detail?.categoryId;
      if (catId) {
        setSearchCategory(catId);
      } else {
        setSearchCategory(null);
      }
      setShowSearchModal(true);
    };
    window.addEventListener('openSearchWithCategory', handleOpenSearch as (e: Event) => void);
    return () => window.removeEventListener('openSearchWithCategory', handleOpenSearch as (e: Event) => void);
  }, []);

  // Reset search-row on route change
  useEffect(() => {
    showSearchRowRef.current = true;
    setShowSearchRow(true);
    lastScrollY.current = window.scrollY;
    scrollIgnoreUntil.current = Date.now() + 300;
  }, [location.pathname]);

  // Hide search row on scroll down, show on scroll up (immediate — no wait for scroll end)
  useEffect(() => {
    if (location.pathname === '/categories') {
      showSearchRowRef.current = true;
      setShowSearchRow(true);
      return;
    }

    const onScroll = () => {
      const y = window.scrollY;
      if (Date.now() < scrollIgnoreUntil.current) {
        lastScrollY.current = y;
        return;
      }

      const delta = y - lastScrollY.current;
      lastScrollY.current = y;

      if (y <= 16) {
        if (!showSearchRowRef.current) {
          showSearchRowRef.current = true;
          scrollIgnoreUntil.current = Date.now() + HEADER_ANIM_MS + 50;
          setShowSearchRow(true);
        }
        return;
      }

      if (delta > 5) {
        if (showSearchRowRef.current) {
          showSearchRowRef.current = false;
          scrollIgnoreUntil.current = Date.now() + HEADER_ANIM_MS + 50;
          setShowSearchRow(false);
        }
      } else if (delta < -5) {
        if (!showSearchRowRef.current) {
          showSearchRowRef.current = true;
          scrollIgnoreUntil.current = Date.now() + HEADER_ANIM_MS + 50;
          setShowSearchRow(true);
        }
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userType');
    localStorage.removeItem('userName');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userPhone');
    localStorage.removeItem('userAvatar');
    localStorage.removeItem('vendorId');
    setUser(null);
    setShowSettingsMenu(false);
    navigate('/login');
    window.dispatchEvent(new Event('authChanged'));
  };

  const handleOpenAddress = () => {
    window.dispatchEvent(new CustomEvent('openAddressModal'));
  };

  const handleOpenTime = () => {
    setShowDateTimeModal(true);
  };

  const setPickupAndSave = (mode: 'courier' | 'scheduled' | 'self') => {
    setPickupMode(mode);
    localStorage.setItem('pickupMode', mode);
    if (mode === 'scheduled') {
      setShowDateTimeModal(true);
    }
  };

  const sendAiMessage = async () => {
    const text = aiInput.trim();
    if (!text || aiLoading) return;
    const userMsg = { role: 'user' as const, content: text };
    setAiMessages(prev => [...prev, userMsg]);
    setAiInput('');
    setAiLoading(true);
    try {
      await new Promise((r) => setTimeout(r, 400));
      setAiMessages(prev => [...prev, {
        role: 'assistant',
        content: t('aiAssistant.offline') || 'AI assistant is currently offline. Please try again later.',
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[55] transition-[height,transform,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          showAIModal ? 'scale-[0.96] opacity-50 pointer-events-none' : ''
        } ${isSearchExpanded ? 'bottom-0 flex flex-col' : ''}`}
        style={
          isSearchExpanded || isStoresPage
            ? undefined
            : { height: showSearchRow ? HEADER_H_EXPANDED : HEADER_H_COLLAPSED }
        }
      >
        <div
          className={`w-full backdrop-blur-xl border-b transition-colors duration-300 flex-shrink-0 h-full ${
            isSearchExpanded ? 'flex flex-col' : ''
          } ${
            isDarkMode
              ? 'bg-dark-100/60 border-dark-200/20'
              : 'bg-white/60 border-stone-200/20'
          }`}
        >
          <div
            className={`max-w-7xl mx-auto px-3 md:px-4 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              isSearchExpanded
                ? 'pt-1 pb-2 space-y-1.5'
                : showSearchRow
                  ? 'pt-2 md:pt-3 pb-2 space-y-1.5 md:space-y-2'
                  : 'pt-2 pb-1.5 space-y-0'
            }`}
          >
            {/* === Row 1: Address + Delivery + Map + Settings === */}
            <AnimatePresence initial={false}>
              {!isSearchExpanded && (
                <motion.div
                  key="header-row1"
                  initial={{ height: 0, opacity: 0, marginTop: 0 }}
                  animate={{ height: 'auto', opacity: 1, marginTop: 0 }}
                  exit={{ height: 0, opacity: 0, marginTop: 0 }}
                  transition={{
                    height: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
                    opacity: { duration: 0.25, ease: 'easeOut' },
                  }}
                  className="overflow-hidden"
                >
                <div className="flex items-center justify-between pb-0">
                {/* Address */}
                <button
                  onClick={handleOpenAddress}
                  className="flex items-center gap-1.5 min-w-0 cursor-pointer group"
                  title={savedAddress || t('header.selectLocation')}
                >
                  <i className="ri-map-pin-fill text-gold flex-shrink-0 text-sm" />
                  <span className={`text-sm font-bold truncate max-w-[160px] ${isDarkMode ? 'text-stone-100' : 'text-stone-900'}`}>
                    {savedAddress ? savedAddress.split(',')[0] : ''}
                  </span>
                  <i className={`ri-arrow-down-s-line text-xs transition-transform group-hover:translate-y-0.5 ${isDarkMode ? 'text-stone-300' : 'text-stone-500'}`} />
                </button>

                {/* Right: Delivery Toggle + Map + Settings */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Delivery / Pickup / Scheduled Toggle Pills */}
                  <div
                    ref={modeContainerRef}
                    className={`relative flex items-center gap-1 rounded-[20px] border backdrop-blur-xl p-1 ${
                      isDarkMode
                        ? 'border-gold/20 bg-gold/5'
                        : 'border-gold/20 bg-gold/3'
                    }`}
                    style={{ direction: 'ltr' }}
                  >
                    {/* Courier */}
                    <div className="relative">
                      {pickupMode === 'courier' && (
                        <motion.div
                          layoutId="modePill"
                          transition={{ type: 'spring', stiffness: 380, damping: 30, mass: 0.8 }}
                          className="absolute inset-0 rounded-[18px] bg-gradient-to-r from-gold-dark to-gold-light pointer-events-none"
                        />
                      )}
                      <button
                        onClick={() => setPickupAndSave('courier')}
                        className={`relative flex items-center gap-1 px-2 h-7 rounded-full transition-colors duration-300 cursor-pointer whitespace-nowrap z-10 ${
                          pickupMode === 'courier'
                            ? 'text-white'
                            : isDarkMode
                              ? 'text-stone-200 hover:bg-dark-200/20'
                              : 'text-stone-500 hover:bg-stone-100/50'
                        }`}
                        title={t('header.delivery')}
                      >
                        <i className={pickupMode === 'courier' ? 'ri-riding-fill' : 'ri-riding-line'} style={{ fontSize: '15px' }} />
                        <AnimatePresence mode="wait">
                          {pickupMode === 'courier' && (
                            <motion.span
                              key="courier-label"
                              initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                              animate={{ opacity: 1, width: 'auto', marginLeft: 4 }}
                              exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                              transition={{ duration: 0.2, ease: 'easeOut' }}
                              className="text-[11px] font-medium overflow-hidden whitespace-nowrap"
                            >
                              {t('header.delivery')}
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </button>
                    </div>

                    {/* Scheduled */}
                    <div className="relative">
                      {pickupMode === 'scheduled' && (
                        <motion.div
                          layoutId="modePill"
                          transition={{ type: 'spring', stiffness: 380, damping: 30, mass: 0.8 }}
                          className="absolute inset-0 rounded-[18px] bg-gradient-to-r from-gold-dark to-gold-light pointer-events-none"
                        />
                      )}
                      <button
                        onClick={() => setPickupAndSave('scheduled')}
                        className={`relative flex items-center gap-1 px-2 h-7 rounded-full transition-colors duration-300 cursor-pointer whitespace-nowrap z-10 ${
                          pickupMode === 'scheduled'
                            ? 'text-white'
                            : isDarkMode
                              ? 'text-stone-200 hover:bg-dark-200/20'
                              : 'text-stone-500 hover:bg-stone-100/50'
                        }`}
                        title={formattedScheduleDate || t('header.schedule')}
                      >
                        <i className={pickupMode === 'scheduled' ? 'ri-calendar-fill' : 'ri-calendar-line'} style={{ fontSize: '15px' }} />
                        <AnimatePresence mode="wait">
                          {pickupMode === 'scheduled' && (
                            <motion.span
                              key="scheduled-label"
                              initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                              animate={{ opacity: 1, width: 'auto', marginLeft: 4 }}
                              exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                              transition={{ duration: 0.2, ease: 'easeOut' }}
                              className="text-[11px] font-medium overflow-hidden whitespace-nowrap"
                            >
                              {formattedScheduleDate
                                ? formattedScheduleDate
                                : `${t('header.schedule')} · ${scheduledSubMode === 'self' ? t('header.pickup') : t('header.delivery')}`
                            }
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </button>
                  </div>

                  {/* Self Pickup */}
                  <div className="relative">
                    {pickupMode === 'self' && (
                      <motion.div
                        layoutId="modePill"
                        transition={{ type: 'spring', stiffness: 380, damping: 30, mass: 0.8 }}
                        className="absolute inset-0 rounded-[18px] bg-gradient-to-r from-gold-dark to-gold-light pointer-events-none"
                      />
                    )}
                    <button
                      onClick={() => setPickupAndSave('self')}
                      className={`relative flex items-center gap-1 px-2 h-7 rounded-full transition-colors duration-300 cursor-pointer whitespace-nowrap z-10 ${
                        pickupMode === 'self'
                          ? 'text-white'
                          : isDarkMode
                            ? 'text-stone-200 hover:bg-dark-200/20'
                            : 'text-stone-500 hover:bg-stone-100/50'
                      }`}
                      title={t('header.pickup')}
                    >
                      <i className={pickupMode === 'self' ? 'ri-walk-fill' : 'ri-walk-line'} style={{ fontSize: '15px' }} />
                      <AnimatePresence mode="wait">
                        {pickupMode === 'self' && (
                          <motion.span
                            key="pickup-label"
                            initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                            animate={{ opacity: 1, width: 'auto', marginLeft: 4 }}
                            exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                            className="text-[11px] font-medium overflow-hidden whitespace-nowrap"
                          >
                            {t('header.pickup')}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </button>
                  </div>
                </div>
              </div>
                </div>
              </motion.div>
            )}
            </AnimatePresence>

            {/* === Row 2: Search + Filter + AI (collapses with header) === */}
            {!isStoresPage && (
            <div
              className="grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
              style={{
                gridTemplateRows: showSearchRow || isSearchExpanded ? '1fr' : '0fr',
                opacity: showSearchRow || isSearchExpanded ? 1 : 0,
              }}
            >
            <div className={`min-h-0 overflow-hidden ${showSearchRow || isSearchExpanded ? '' : 'pointer-events-none'}`}>
            <AnimatePresence mode="wait">
              {isSearchExpanded ? (
                <motion.div
                  key="search-expanded-panel"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.12, ease: 'easeOut' }}
                  className="flex items-center gap-2 flex-1"
                >
                  {/* Back Button */}
                  <motion.button
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => { setIsSearchExpanded(false); setSearchQuery(''); }}
                    className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors ${
                      isDarkMode ? 'bg-dark-200/60 hover:bg-dark-200 text-gray-300' : 'bg-stone-100 hover:bg-stone-200 text-gray-700'
                    }`}
                  >
                    <i className="ri-arrow-left-line text-lg" />
                  </motion.button>

                  {/* Search Input — shared layoutId so it morphs in place from the collapsed bar */}
                  <div
                    className={`flex-1 relative rounded-xl overflow-hidden ${
                    isDarkMode ? 'bg-dark-200/50 border-dark-200/30' : 'bg-stone-100 border-stone-200/30'
                  } border`}>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && searchQuery.trim()) {
                          setIsSearchExpanded(false);
                          navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                        }
                      }}
                      placeholder=""
                      className={`w-full px-4 py-2.5 bg-transparent outline-none text-sm ${isDarkMode ? 'text-stone-100' : 'text-stone-900'}`}
                    />
                    {/* Animated placeholder overlay */}
                    {!searchQuery && (
                      <div className="absolute inset-0 px-4 py-2.5 pointer-events-none">
                        <span className={`text-sm font-medium truncate ${isDarkMode ? 'text-stone-400' : 'text-stone-500'}`}>
                          {searchItem ? (
                            <span className="inline-flex items-baseline">
                              <span className="whitespace-nowrap">{t('header.searchingFor')}&nbsp;</span>
                              <span className="relative inline-flex items-baseline overflow-hidden" style={{ minHeight: '1.2em' }}>
                                <AnimatePresence mode="wait" initial={false}>
                                  <motion.span
                                    key={searchItem.text}
                                    initial={{ y: '110%', opacity: 0 }}
                                    animate={{ y: '0%', opacity: 1 }}
                                    exit={{ y: '-110%', opacity: 0 }}
                                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                    className="inline-flex whitespace-nowrap"
                                  >
                                    {searchItem.text}؟
                                  </motion.span>
                                </AnimatePresence>
                              </span>
                            </span>
                          ) : (
                            t('header.searchPlaceholder')
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Filter Button */}
                  <motion.button
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.2, delay: 0.05 }}
                    onClick={() => setInlineFilterTrigger(prev => prev + 1)}
                    className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors ${
                      isDarkMode
                        ? 'bg-dark-200/60 hover:bg-dark-200 text-gray-300'
                        : 'bg-stone-100 hover:bg-stone-200 text-gray-700'
                    }`}
                    title={t('search.filters')}
                  >
                    <i className="ri-equalizer-line text-lg" />
                  </motion.button>
                </motion.div>
              ) : (
                <motion.div
                  key="search-collapsed-panel"
                  initial={{ opacity: 1, y: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.1 }}
                  className="flex items-center gap-2 flex-1"
                >
                  {/* Search Bar (collapsed) */}
                  {!showSearchModal && (
                  <motion.button
                    onClick={() => setIsSearchExpanded(true)}
                    className={`flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer rounded-full px-4 py-2.5 transition-all border ${
                      isDarkMode
                        ? 'bg-dark-200/50 border-dark-200/20 hover:bg-dark-200/70'
                        : 'bg-stone-100/80 border-stone-200/20 hover:bg-stone-100'
                    }`}
                  >
                    <i className="ri-search-line text-gold text-sm" />
                    <span className={`text-sm font-medium truncate ${isDarkMode ? 'text-stone-300' : 'text-stone-500'}`}>
                      {isSearchPage && searchPageQuery ? (
                        <span className="inline-flex items-baseline gap-1">
                          <span className="whitespace-nowrap text-gold">{t('search.for') || 'برای'}</span>
                          <span className="truncate">&ldquo;{searchPageQuery}&rdquo;</span>
                        </span>
                      ) : searchItem ? (
                        <span className="inline-flex items-baseline">
                          <span className="whitespace-nowrap">{t('header.searchingFor')}&nbsp;</span>
                          <span className="relative inline-flex items-baseline overflow-hidden" style={{ minHeight: '1.2em' }}>
                            <AnimatePresence mode="wait" initial={false}>
                              <motion.span
                                key={searchItem.text}
                                initial={{ y: '110%', opacity: 0 }}
                                animate={{ y: '0%', opacity: 1 }}
                                exit={{ y: '-110%', opacity: 0 }}
                                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                className="inline-flex whitespace-nowrap"
                              >
                                {searchItem.text}?
                              </motion.span>
                            </AnimatePresence>
                          </span>
                        </span>
                      ) : (
                        t('header.searchPlaceholder')
                      )}
                    </span>
                  </motion.button>
                  )}

                  {/* Filter Button */}
                  <button
                    onClick={() => {
                      setShowSearchWithFilters(true);
                      setShowSearchModal(true);
                    }}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all cursor-pointer border flex-shrink-0 ${
                      isDarkMode
                        ? 'bg-dark-200/40 border-dark-200/20 text-stone-300 hover:text-gold hover:border-gold/30'
                        : 'bg-stone-100/80 border-stone-200/20 text-stone-500 hover:text-gold hover:border-gold/30'
                    }`}
                    title={t('search.filters')}
                  >
                    <i className={`ri-equalizer-line text-sm ${isDarkMode ? 'text-stone-300' : 'text-stone-500'}`} />
                  </button>

                  {/* AI Assistant Button — single 4-point star that spins 90° at random intervals + ripple burst */}
                <div className="relative flex-shrink-0">
                    <button
                      onClick={() => setShowAIModal(true)}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all cursor-pointer border relative ${
                        isDarkMode
                          ? 'bg-dark-200/40 border-dark-200/20 text-stone-300 hover:text-gold hover:border-gold/30'
                          : 'bg-stone-100/80 border-stone-200/20 text-stone-500 hover:text-gold hover:border-gold/30'
                      }`}
                      title={t('aiAssistant.title')}
                    >
                      <div className="relative w-[18px] h-[18px] flex items-center justify-center">
                        <motion.svg
                          width="18"
                          height="18"
                          viewBox="0 0 14 14"
                          fill="currentColor"
                          className="text-gold relative z-10"
                          animate={{ rotate: aiStarRotation }}
                          transition={{ duration: 0.7, ease: [0.34, 1.4, 0.64, 1] }}
                        >
                          <path fillRule="evenodd" clipRule="evenodd" d="M7.93488 0.654773C7.6174 -0.218303 6.38263 -0.218294 6.06515 0.654774L4.6224 4.62235 0.654818 6.06511c-0.873075 0.31748 -0.873066 1.55224 0.000002 1.86972L4.6224 9.37759l1.44275 3.96761c0.31748 0.873 1.55225 0.873 1.86973 0l1.44275 -3.96761 3.96757 -1.44276c0.8731 -0.31748 0.8731 -1.55224 0 -1.86972L9.37763 4.62235 7.93488 0.654773Z" />
                        </motion.svg>
                        <AnimatePresence>
                          <motion.svg
                            key={rippleKey}
                            width="18"
                            height="18"
                            viewBox="0 0 14 14"
                            className="absolute inset-0 m-auto pointer-events-none"
                            initial={{ scale: 1, opacity: 0 }}
                            animate={{ scale: 2.6, opacity: [0, 0.75, 0] }}
                            exit={{ opacity: 0 }}
                            transition={{
                              duration: 0.75,
                              ease: 'easeOut',
                              opacity: { times: [0, 0.12, 1] },
                            }}
                          >
                            <defs>
                              <linearGradient id={`rainbow-${rippleKey}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#ef4444" />
                                <stop offset="17%" stopColor="#f97316" />
                                <stop offset="33%" stopColor="#eab308" />
                                <stop offset="50%" stopColor="#22c55e" />
                                <stop offset="67%" stopColor="#3b82f6" />
                                <stop offset="83%" stopColor="#a855f7" />
                                <stop offset="100%" stopColor="#ec4899" />
                              </linearGradient>
                            </defs>
                            <path
                              fill="none"
                              stroke={`url(#rainbow-${rippleKey})`}
                              strokeWidth="0.4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M7.93488 0.654773C7.6174 -0.218303 6.38263 -0.218294 6.06515 0.654774L4.6224 4.62235 0.654818 6.06511c-0.873075 0.31748 -0.873066 1.55224 0.000002 1.86972L4.6224 9.37759l1.44275 3.96761c0.31748 0.873 1.55225 0.873 1.86973 0l1.44275 -3.96761 3.96757 -1.44276c0.8731 -0.31748 0.8731 -1.55224 0 -1.86972L9.37763 4.62235 7.93488 0.654773Z"
                            />
                          </motion.svg>
                        </AnimatePresence>
                      </div>
                    </button>
                  </div>

                  {/* Theme Toggle - Mobile only */}
                  <button
                    onClick={toggleTheme}
                    className={`md:hidden w-10 h-10 rounded-full flex items-center justify-center transition-all cursor-pointer border flex-shrink-0 ${
                      isDarkMode
                        ? 'bg-dark-200/40 border-dark-200/20 text-amber-300 hover:text-gold hover:border-gold/30'
                        : 'bg-stone-100/80 border-stone-200/20 text-gold-dark hover:text-gold hover:border-gold/30'
                    }`}
                    title={isDarkMode ? (t('theme.lightMode') || 'Light mode') : (t('theme.darkMode') || 'Dark mode')}
                  >
                    <i className={`text-sm ${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'}`} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
            </div>
            </div>
            )}
          </div>
        </div>

        {/* Settings Dropdown Menu */}
        <AnimatePresence>
          {showSettingsMenu && (
            <motion.div
              ref={menuRef}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className={`absolute top-full ${isRTL ? 'left-4' : 'right-4'} mt-2 w-64 rounded-2xl border shadow-xl overflow-hidden z-[160] ${
                isDarkMode
                  ? 'bg-dark-100/80 border-dark-200/30 backdrop-blur-xl'
                  : 'bg-white/80 border-stone-200/30 backdrop-blur-xl'
              }`}
            >
              {/* User Info */}
              {user && (
                <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-dark-200/50' : 'border-gray-200/50'}`}>
                  <div className="flex items-center gap-3">
                    {user.avatar ? (
                      <img src={resolveImageUrl(user.avatar)} alt={user.name} className="w-9 h-9 rounded-xl object-cover flex-shrink-0" draggable={false} />
                    ) : (
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 ${isDarkMode ? 'bg-gold/15 text-gold' : 'bg-gold/10 text-gold'}`}>
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{user.name}</p>
                      <p className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{user.userType === 'vendor' ? t('nav.vendor') : t('header.customer')}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="py-1">
                {user && (
                  <>
                    <button onClick={() => { setShowSettingsMenu(false); navigate('/profile'); }} className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${isDarkMode ? 'hover:bg-dark-200/50 text-gray-200' : 'hover:bg-gray-100/50 text-gray-900'}`}>
                      <i className="ri-user-line text-gold text-base" />{t('nav.profile')}
                    </button>
                    {user.userType === 'courier' && (
                      <button onClick={() => { setShowSettingsMenu(false); navigate('/courier'); }} className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${isDarkMode ? 'hover:bg-dark-200/50 text-gray-200' : 'hover:bg-gray-100/50 text-gray-900'}`}>
                        <i className="ri-truck-line text-gold text-base" />Courier
                      </button>
                    )}
                    <button onClick={() => { setShowSettingsMenu(false); navigate('/orders'); }} className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${isDarkMode ? 'hover:bg-dark-200/50 text-gray-200' : 'hover:bg-gray-100/50 text-gray-900'}`}>
                      <i className="ri-shopping-bag-line text-gold text-base" />{t('nav.orders')}
                    </button>
                    <button onClick={() => { setShowSettingsMenu(false); navigate('/notifications'); }} className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${isDarkMode ? 'hover:bg-dark-200/50 text-gray-200' : 'hover:bg-gray-100/50 text-gray-900'}`}>
                      <i className="ri-notification-3-line text-gold text-base" />{t('nav.notifications')}
                      {unreadCount > 0 && <span className="ml-auto bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-semibold">{unreadCount > 99 ? '99+' : unreadCount}</span>}
                    </button>
                    <div className={`mx-4 h-px my-1 ${isDarkMode ? 'bg-dark-200/50' : 'bg-gray-200/50'}`} />
                  </>
                )}

                {/* Language */}
                <button onClick={() => setShowLangMenu(!showLangMenu)} className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${isDarkMode ? 'hover:bg-dark-200/50 text-gray-200' : 'hover:bg-gray-100/50 text-gray-900'}`}>
                  <i className="ri-global-line text-gold text-base" />
                  <span className="flex-1">{t('nav.language')}</span>
<motion.i animate={{ rotate: showLangMenu ? 180 : 0 }} transition={{ duration: 0.2 }} className={`ri-arrow-down-s-line ${isDarkMode ? 'text-stone-400' : 'text-gray-400'}`} />
                </button>
                <AnimatePresence>
                  {showLangMenu && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                      <div className={`px-3 pb-2 ${isDarkMode ? 'bg-dark-100/30' : 'bg-gray-50/50'}`}>
                        {langs.map((l) => (
                          <button key={l.code} onClick={() => { setLanguage(l.code); setShowLangMenu(false); }} className={`w-full px-3 py-2 rounded-xl text-left text-sm flex items-center gap-2 transition-colors ${language === l.code ? (isDarkMode ? 'bg-gold/15 text-gold' : 'bg-gold/10 text-gold') : (isDarkMode ? 'hover:bg-dark-200/50 text-gray-300' : 'hover:bg-gray-100 text-gray-700')}`}>
                            <span className="text-base">{l.flag}</span>{l.name}
                            {language === l.code && <i className="ri-check-line text-gold ml-auto" />}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Dark Mode */}
                <button onClick={() => { if (propSetIsDarkMode) propSetIsDarkMode(!isDarkMode); else toggleTheme(); }} className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${isDarkMode ? 'hover:bg-dark-200/50 text-gray-200' : 'hover:bg-gray-100/50 text-gray-900'}`}>
                  <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} text-gold text-base`} />{isDarkMode ? t('theme.lightMode') : t('theme.darkMode')}
                </button>

                {/* Help */}
                <button onClick={() => { setShowSettingsMenu(false); navigate('/help-support'); }} className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${isDarkMode ? 'hover:bg-dark-200/50 text-gray-200' : 'hover:bg-gray-100/50 text-gray-900'}`}>
                  <i className="ri-question-line text-gold text-base" />{t('nav.help')}
                </button>

                <div className={`mx-4 h-px my-1 ${isDarkMode ? 'bg-dark-200/50' : 'bg-gray-200/50'}`} />

                {/* Address */}
                <button onClick={() => { setShowSettingsMenu(false); handleOpenAddress(); }} className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${isDarkMode ? 'hover:bg-dark-200/50 text-gray-200' : 'hover:bg-gray-100/50 text-gray-900'}`}>
                  <i className="ri-map-pin-line text-gold text-base" />{t('header.address')}
                </button>

                <div className={`mx-4 h-px my-1 ${isDarkMode ? 'bg-dark-200/50' : 'bg-gray-200/50'}`} />

                {user ? (
                  <button onClick={handleLogout} className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${isDarkMode ? 'hover:bg-red-500/10 text-red-400' : 'hover:bg-red-50 text-red-500'}`}>
<i className={`ri-logout-box-r-line text-base ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />{t('auth.logout')}
                  </button>
                ) : (
                  <button onClick={() => { setShowSettingsMenu(false); navigate('/login'); }} className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${isDarkMode ? 'hover:bg-gold/10 text-gold' : 'hover:bg-gold/10 text-gold'}`}>
<i className={`ri-login-box-line text-base ${isDarkMode ? 'text-gold' : 'text-gold'}`} />{t('auth.login')}
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Inline Search Results — rendered INSIDE the header flex column, directly
            below the search bar. Because the bar is flex-shrink-0 at the top and the
            results fill the remaining flex-1 space, the bar's resting position is
            identical whether results are present or not → zero positional jump. */}
        <AnimatePresence>
          {isSearchExpanded && (
            <motion.div
              key="inline-results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className={`flex-1 min-h-0 overflow-y-auto ${isDarkMode ? 'bg-dark-100/40' : 'bg-white/40'} backdrop-blur-lg`}
            >
              <SearchModal
                isOpen={true}
                onClose={() => { setIsSearchExpanded(false); setSearchQuery(''); }}
                isDarkMode={isDarkMode}
                inline={true}
                externalQuery={searchQuery}
                filterOpenTrigger={inlineFilterTrigger}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Fixed spacer — height does NOT follow search collapse; content stays put */}
      <div
        className="w-full shrink-0"
        style={{ height: isStoresPage ? 50 : HEADER_SPACER_H }}
        aria-hidden
      />

      {/* Search Modal (legacy modal mode) */}
      <SearchModal
        isOpen={showSearchModal}
        onClose={() => {
          setShowSearchModal(false);
          setShowSearchWithFilters(false);
        }}
        isDarkMode={isDarkMode}
        preselectedCategory={searchCategory}
        onClearCategory={() => setSearchCategory(null)}
        initialShowFilters={showSearchWithFilters}
      />

      {/* DateTime Modal */}
      <DateTimeModal isOpen={showDateTimeModal} onClose={() => setShowDateTimeModal(false)} isDarkMode={isDarkMode} />

      {/* AI Assistant Modal */}
      <AnimatePresence>
        {showAIModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] flex items-end justify-center"
            onClick={() => setShowAIModal(false)}
          >
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-black/60' : 'bg-white/60'} backdrop-blur-sm`} />
            <motion.div
              ref={aiModalRef}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className={`relative w-full md:max-w-md md:rounded-2xl rounded-t-3xl ${isDarkMode ? 'bg-dark-100/80 border-dark-200/30 backdrop-blur-xl' : 'bg-white/80 border-stone-200/30 backdrop-blur-xl'} border shadow-2xl max-h-[80vh] overflow-hidden flex flex-col`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-1">
                <div className={`w-10 h-1 rounded-full ${isDarkMode ? 'bg-dark-300' : 'bg-gray-300'}`} />
              </div>

              {/* Header */}
              <div className={`flex items-center justify-between px-5 pb-3 border-b ${isDarkMode ? 'border-dark-200/50' : 'border-gray-200/50'}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-gold/15' : 'bg-gold/10'}`}>
                    <svg width="16" height="16" viewBox="0 0 14 14" fill="currentColor" className="text-gold">
                      <path fillRule="evenodd" clipRule="evenodd" d="M7.93488 0.654773C7.6174 -0.218303 6.38263 -0.218294 6.06515 0.654774L4.6224 4.62235 0.654818 6.06511c-0.873075 0.31748 -0.873066 1.55224 0.000002 1.86972L4.6224 9.37759l1.44275 3.96761c0.31748 0.873 1.55225 0.873 1.86973 0l1.44275 -3.96761 3.96757 -1.44276c0.8731 -0.31748 0.8731 -1.55224 0 -1.86972L9.37763 4.62235 7.93488 0.654773Z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className={`font-bold text-sm ${isDarkMode ? 'text-stone-100' : 'text-stone-900'}`}>{t('aiAssistant.title')}</h3>
                    <p className={`text-[10px] ${isDarkMode ? 'text-stone-400' : 'text-stone-500'}`}>{t('aiAssistant.subtitle')}</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowAIModal(false); setAiMessages([]); }}
                  className={`w-8 h-8 rounded-xl flex items-center justify-center cursor-pointer ${isDarkMode ? 'bg-dark-200/30 hover:bg-dark-300/40 text-stone-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                >
                  <i className={`ri-close-line ${isDarkMode ? 'text-stone-300' : 'text-gray-700'}`} />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                {/* Quick Actions - only show when no messages */}
                {aiMessages.length === 0 && (
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {[
                      { icon: 'ri-fire-line', label: t('aiAssistant.hotDeals'), path: '/categories', color: isDarkMode ? 'text-orange-300' : 'text-orange-500' },
                      { icon: 'ri-truck-line', label: t('aiAssistant.trackOrder'), path: '/orders', color: isDarkMode ? 'text-sky-300' : 'text-sky-500' },
                      { icon: 'ri-star-line', label: t('aiAssistant.popular'), path: '/', color: isDarkMode ? 'text-amber-300' : 'text-amber-500' },
                      { icon: 'ri-store-2-line', label: t('aiAssistant.findStores'), path: '/stores', color: isDarkMode ? 'text-emerald-300' : 'text-emerald-500' },
                    ].map((action) => (
                      <button
                        key={action.path}
                        onClick={() => {
                          setShowAIModal(false);
                          navigate(action.path);
                        }}
                        className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all cursor-pointer ${
                          isDarkMode
                            ? 'bg-dark-200/20 border-dark-200/20 hover:bg-dark-200/40'
                            : 'bg-white/40 border-stone-200/20 hover:bg-white/60'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-dark-200/40' : 'bg-stone-100'}`}>
                          <i className={`${action.icon} ${action.color} text-lg`} />
                        </div>
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-stone-200' : 'text-stone-800'}`}>{action.label}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Chat Messages */}
                {aiMessages.length > 0 && (
                  <div className="space-y-3">
                    {aiMessages.map((msg, i) => (
                      <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                          msg.role === 'user'
                            ? (isDarkMode ? 'bg-gold/20 text-stone-100 rounded-br-md' : 'bg-gold/10 text-stone-800 rounded-br-md')
                            : (isDarkMode ? 'bg-dark-200/60 text-stone-100 rounded-bl-md' : 'bg-stone-100 text-stone-800 rounded-bl-md')
                        }`}>
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      </div>
                    ))}
                    {aiLoading && (
                      <div className="flex justify-start">
                        <div className={`rounded-2xl rounded-bl-md px-4 py-3 ${isDarkMode ? 'bg-dark-200/60' : 'bg-stone-100'}`}>
                          <div className="flex items-center gap-1.5">
                            <div className={`w-2 h-2 rounded-full animate-bounce ${isDarkMode ? 'bg-gold' : 'bg-gold'}`} style={{ animationDelay: '0ms' }} />
                            <div className={`w-2 h-2 rounded-full animate-bounce ${isDarkMode ? 'bg-gold' : 'bg-gold'}`} style={{ animationDelay: '150ms' }} />
                            <div className={`w-2 h-2 rounded-full animate-bounce ${isDarkMode ? 'bg-gold' : 'bg-gold'}`} style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={aiMessagesEndRef} />
                  </div>
                )}

                {/* Welcome message when empty */}
                {aiMessages.length === 0 && (
                  <div className={`mt-4 p-4 rounded-xl border ${isDarkMode ? 'bg-dark-200/20 border-dark-200/20' : 'bg-stone-50/80 border-stone-200/20'}`}>
                    <p className={`text-xs text-center ${isDarkMode ? 'text-stone-300' : 'text-stone-600'}`}>
                      <i className="ri-sparkling-line text-gold mr-1" />
                      {t('aiAssistant.askAnything')}
                    </p>
                    <p className="text-center mt-1">
                      <AiTypingSubtitle isDarkMode={isDarkMode} />
                    </p>
                  </div>
                )}
              </div>

              {/* Input */}
              <div className={`p-4 border-t ${isDarkMode ? 'border-dark-200/50' : 'border-gray-200/50'}`}>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all ${
                  isDarkMode
                    ? 'bg-dark-200/20 border-dark-200/20 focus-within:border-gold/40'
                    : 'bg-white/40 border-stone-200/20 focus-within:border-gold/40'
                }`}>
                  <i className="ri-sparkling-line text-gold text-sm flex-shrink-0" />
                  <input
                    type="text"
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAiMessage(); } }}
                    placeholder={t('aiAssistant.askAnything')}
                    disabled={aiLoading}
                    className={`flex-1 bg-transparent text-sm outline-none ${isDarkMode ? 'text-stone-100 placeholder-stone-500' : 'text-stone-700 placeholder-stone-400'}`}
                  />
                  <button
                    onClick={sendAiMessage}
                    disabled={!aiInput.trim() || aiLoading}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                      aiInput.trim() && !aiLoading
                        ? (isDarkMode ? 'bg-gold/20 text-gold hover:bg-gold/30' : 'bg-gold/10 text-gold hover:bg-gold/20')
                        : (isDarkMode ? 'bg-dark-200/40 text-stone-600' : 'bg-stone-200 text-stone-400')
                    }`}
                  >
                    <i className="ri-arrow-up-line text-sm" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}